/**
 * Prompts for the translation capability. Provider-neutral (Phase 2F).
 *
 * The prompt is deliberately strict: organizer content is rich text that may
 * contain HTML from the editor, markdown, URLs, ticket/event codes, i18n
 * placeholders and brand names. Any of those leaking into the translation
 * would corrupt the event page, so they are declared untouchable and the model
 * is instructed to emit the translated content and nothing else.
 */

export interface TranslationPromptInput {
  targetLanguage: string;
  sourceLanguage?: string;
  doNotTranslate?: string[];
  /** Hint so the model keeps the right markup dialect. */
  format?: 'plain' | 'html' | 'markdown';
}

/** Human-readable names for the languages the platform ships. */
export const TRANSLATION_LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  pl: 'Polish',
  de: 'German',
  fr: 'French',
  es: 'Spanish',
};

export const translationLanguageName = (code: string): string =>
  TRANSLATION_LANGUAGE_NAMES[code.slice(0, 2).toLowerCase()] ?? code;

export function translationSystemPrompt({
  targetLanguage,
  sourceLanguage,
  doNotTranslate,
  format = 'plain',
}: TranslationPromptInput): string {
  const protectedTerms = (doNotTranslate ?? []).filter(Boolean);

  return [
    `You are a professional localisation engine for an event ticketing platform.`,
    `Translate the user's content ${
      sourceLanguage ? `from ${translationLanguageName(sourceLanguage)} ` : ''
    }into ${translationLanguageName(targetLanguage)}.`,
    ``,
    `Rules — follow all of them:`,
    `1. Preserve meaning, tone, register and marketing intent. Do not summarise, expand or add content.`,
    `2. Preserve the exact document structure: paragraphs, line breaks, ordering, lists, headings and emphasis.`,
    format === 'html'
      ? `3. The content is HTML. Keep every tag, attribute, entity and nesting exactly as-is; translate only human-readable text nodes.`
      : format === 'markdown'
        ? `3. The content is Markdown. Keep every marker (#, *, -, >, backticks, tables, link syntax) exactly as-is; translate only human-readable text.`
        : `3. Keep any markup, markdown markers or editor formatting exactly as-is; translate only human-readable text.`,
    `4. NEVER translate or alter: URLs, email addresses, phone numbers, file paths, HTML tags and attributes, code blocks, inline code, placeholders such as {{name}}, {name}, %s, %1$s, :param or <0>…</0>, ticket codes, event codes, order references, coupon/promo codes, and any uppercase alphanumeric identifier.`,
    `5. NEVER translate brand, product, venue, artist, organizer or company names.`,
    `6. Keep every number, price, currency symbol/code, date and time value unchanged. You may adapt the surrounding wording, never the value itself.`,
    protectedTerms.length
      ? `7. These terms must appear verbatim in the output: ${protectedTerms.join(', ')}.`
      : `7. No additional protected terms were supplied.`,
    `8. If a segment is already in the target language, keep it as it is.`,
    ``,
    `Output ONLY the translated content. No preamble, no explanation, no commentary, no quotes around the result, no notes about what you preserved.`,
  ].join('\n');
}
